import { type FC, memo, useCallback, useEffect, useMemo, useState } from "react";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import { AddFeature, RecordParticipationTypes, RemoveFeature, useSimpleFeatureTernary } from "@viagogo/feature";
import { useContent } from "src/shared/components/content";
import { TruncateText } from "src/shared/components/TruncateText";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useAppContext } from "src/shared/context/AppContext";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { getAppTheme, isNaCobrands } from "src/shared/utils/appContextHelper";
import { PERFORMER_IMAGE_SIZES } from "src/website-apps/responsive/stubhub-performer/components/page-body/constants";
import { GenreTags } from "src/website-apps/responsive/stubhub-performer/components/performer-info-card/GenreTags";
import { ImageCarousel } from "src/website-apps/responsive/stubhub-performer/components/performer-info-card/image-carousel/ImageCarousel";
import { PartnerLogoImage } from "src/website-apps/responsive/stubhub-performer/components/performer-info-card/PartnerLogoImage";
import { PerformerCardSocialLinks } from "src/website-apps/responsive/stubhub-performer/components/performer-info-card/PerformerCardSocialLinks";
import { ReigningChampion } from "src/website-apps/responsive/stubhub-performer/components/performer-info-card/reigning-champion/ReigningChampion";
import {
  borderRadiusTop,
  genreTagContainerStyle,
  performerCardSection,
  performerInfoCard,
  socialLinksStyle,
  spotifyPlayerStyle,
  styledImageFlat,
  styledImageRounded,
  theaterRuntimeMargin,
  videoContainerStyle,
} from "src/website-apps/responsive/stubhub-performer/components/performer-info-card/styles";
import { TheaterRuntime } from "src/website-apps/responsive/stubhub-performer/components/performer-info-card/theater-runtime/TheaterRuntime";
import { OfficialMarketplaceBanner } from "src/website-apps/responsive/stubhub-performer/components/performer-info-card/tim-mcgraw-banner/OfficialMarketplaceBanner";
import { UserContentCarousel } from "src/website-apps/responsive/stubhub-performer/components/performer-info-card/user-generated-content/UserContentCarousel";
import { EmbeddedYoutubePlayer } from "src/website-apps/responsive/stubhub-performer/components/performer-youtube-player";
import { SpotifyEmbeddedPlayer } from "src/website-apps/responsive/stubhub-performer/components/spotify-player";
import { usePerformerPageModelData } from "src/website-apps/responsive/stubhub-performer/hooks/usePerformerPageModelData";
import { TOP_LEVEL_CATEGORY_IDS } from "src/website-apps/shared/components/explore-events/constants";
import { TIM_MCGRAW_EXCLUSIVE_CATEGORY_ID } from "src/website-apps/shared/components/hero-banner/ImmersiveHeroBanner/ArtistInfoColumn";
import { PerformerMerchLabel } from "src/website-apps/shared/components/hero-banner/PerformerMerchLabel";
import { Image, ImageSize } from "src/website-apps/shared/components/image";
import { InViewTracker } from "src/website-apps/shared/components/InViewTracker";
import { useIsCalendarView } from "src/website-apps/shared/hooks/useIsCalendarView";
import { useIsMlb } from "src/website-apps/shared/hooks/useIsMlb";
import { usePartnerTheme } from "src/website-apps/shared/hooks/usePartnerTheme";
import { usePerformerInfoCardData } from "src/website-apps/shared/hooks/usePerformerInfoCardData";
import {
  isPerformerCredibilityBadge,
  PerformerLabelType,
  useWillShowPerformerCredibilityLabel,
} from "src/website-apps/shared/hooks/useWillShowPerformerCredibilityLabel";
import { getPartnerColor } from "src/website-apps/shared/utils/getPartnerColor";

const sectionMutedClasses = performerCardSection({ tone: "muted" });
const sectionMutedSmallClasses = performerCardSection({ tone: "muted", size: "small" });

interface Props {
  /** show performer info card with just an image, nothing below it. used for desktop. */
  showOnlyImage?: Maybe<boolean>;
}

/**
 * Card to display information about a performer such as name, bio, social media links, etc.
 */
export const PerformerInfoCardDesktop: FC<Props> = memo(({ showOnlyImage = false }) => {
  const model = usePerformerPageModelData();
  const isTabletBreakpoint = useMediaQuery(Breakpoint.Tablet);
  const isDesktop = useMediaQuery(Breakpoint.Desktop);
  const isMobile = !isTabletBreakpoint;
  const isTablet = isTabletBreakpoint && !isDesktop;
  const appContext = useAppContext();
  const storePageEvent = useStorePageEvent();
  const theme = usePartnerTheme();
  const { isOnCalendarView } = useIsCalendarView();
  const isMlb = useIsMlb();
  const {
    hasEnoughData,
    hasAnyData,
    hasNonModuleData,
    genres,
    bio,
    spotifyTopTrackId,
    youTubeVideoId,
    showDuration,
    catalogSourcedGenre,
    hasReigningChampionData,
    reigningChampions,
    categoryUserGeneratedImages,
  } = usePerformerInfoCardData();
  const [imageLoaded, setImageLoaded] = useState(false);
  const onImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  const maxLinesForBio = useMemo(() => {
    if (isMobile) return 21;
    if (isTablet) return 10;
    return 5;
  }, [isMobile, isTablet]);

  useEffect(() => {
    if (hasEnoughData) {
      // record when loaded so that we can break down people who saw card vs people who just saw shorter grid width
      storePageEvent(PE_TYPE.LOADED, PE_KEYS.PERFORMER_INFO_CARD_LOADED, "");
    }
  }, [hasEnoughData, storePageEvent]);

  const hasInfoCardModuleData = !!(spotifyTopTrackId || youTubeVideoId || catalogSourcedGenre || genres?.length);
  // AB 104179 — participation is recorded IN_VIEW by the sentinel AddFeature rendered alongside the card
  const isInfoCardModuleRemoved =
    useSimpleFeatureTernary(
      FeatureSetEnum.TMPerformerRemoveSpotifyYoutubeGenreTags,
      false,
      true,
      () => !isMobile && !showOnlyImage && hasInfoCardModuleData,
      false,
    ) ?? false;

  const hasNonRemovedData = isInfoCardModuleRemoved ? hasNonModuleData : hasAnyData;

  // Removal telemetry shares the participation in-view boundary so events only fire once the card is seen
  const fireModuleRemovalEvents = useCallback(() => {
    if (!isInfoCardModuleRemoved) return;
    const categoryId = String(model.categorySummary.categoryId ?? "");
    if (spotifyTopTrackId) {
      storePageEvent(PE_TYPE.FEATURETRACKING, PE_KEYS.PERFORMER_INFO_CARD_SPOTIFY_REMOVED, categoryId);
    }
    if (youTubeVideoId) {
      storePageEvent(PE_TYPE.FEATURETRACKING, PE_KEYS.PERFORMER_INFO_CARD_YOUTUBE_REMOVED, categoryId);
    }
  }, [isInfoCardModuleRemoved, spotifyTopTrackId, youTubeVideoId, storePageEvent, model.categorySummary.categoryId]);

  const resolvedSectionClasses =
    useSimpleFeatureTernary(
      FeatureSetEnum.TMPerformerInfoCardSmallerText,
      sectionMutedClasses,
      sectionMutedSmallClasses,
      () => !!hasEnoughData,
    ) ?? sectionMutedClasses;
  const cardClasses = performerInfoCard({
    cardBorder: "none",
    density: "reduced",
    surface: "white",
    shadow: "floating",
  });

  const performerName = model.categorySummary.categoryName;
  const isSportsCategory = model.categorySummary.topLevelCategoryId === TOP_LEVEL_CATEGORY_IDS.SPORT;
  const partnerOrBrandColor =
    getPartnerColor(theme, appContext.partnerTheme) ?? getAppTheme(appContext).colors.backgroundBrandActive;
  const defaultImageAltText = useContent("cid$#4138909#"); // Performer
  const labelType = useWillShowPerformerCredibilityLabel();
  const canMoveCredibilityBadge = isDesktop && isPerformerCredibilityBadge(labelType);
  const shouldMoveCredibilityBadge =
    useSimpleFeatureTernary(
      FeatureSetEnum.TMPerformerCredibilityBadgePlacement,
      false,
      true,
      () => canMoveCredibilityBadge,
      false,
    ) ?? false;
  const showCredibilityBadgeOnCard = labelType !== PerformerLabelType.NONE && !shouldMoveCredibilityBadge;
  return (
    <div className={cardClasses.root}>
      {showCredibilityBadgeOnCard && <PerformerMerchLabel className={borderRadiusTop} />}
      {theme.partnerLogoUrl && isMlb ? (
        <PartnerLogoImage
          partnerLogoUrl={theme.partnerLogoLightSvgUrl ?? theme.partnerLogoUrl}
          partnerColor={partnerOrBrandColor}
          onLoad={onImageLoad}
          isShort={isOnCalendarView}
          noBorderRadius={showCredibilityBadgeOnCard}
        />
      ) : (
        <Image
          alt={model.categorySummary.categoryName}
          src={model.categorySummary.portalImageUrl}
          srcSet={model.categorySummary.portalImageSrcSet ?? undefined}
          sizes={PERFORMER_IMAGE_SIZES}
          size={ImageSize.PortalImage}
          loading="eager"
          fetchPriority="high"
          onLoad={onImageLoad}
          onError={onImageLoad}
          className={showCredibilityBadgeOnCard ? styledImageFlat : styledImageRounded}
        />
      )}
      <AddFeature
        id={FeatureSetEnum.TMPerformerTimMcGrawExlusiveMarketplaceBannerKillswitch}
        canParticipate={() => model.categorySummary.categoryId === TIM_MCGRAW_EXCLUSIVE_CATEGORY_ID}
        feature={() => <OfficialMarketplaceBanner performerName="Tim McGraw" />}
      />
      {!showOnlyImage && hasInfoCardModuleData && (
        <>
          <AddFeature
            id={FeatureSetEnum.TMPerformerRemoveSpotifyYoutubeGenreTags}
            canParticipate={() => !isMobile && hasInfoCardModuleData}
            recordType={RecordParticipationTypes.IN_VIEW}
            feature={() => null}
          />
          <InViewTracker onInView={fireModuleRemovalEvents} />
        </>
      )}
      {showOnlyImage ||
      !imageLoaded ||
      (!hasNonRemovedData && !model.categoryMetadata?.common?.infoCardCarouselImageUrls?.length) ? null : (
        <div className={cardClasses.content}>
          {!isInfoCardModuleRemoved && (!!catalogSourcedGenre || genres) ? (
            <div className={genreTagContainerStyle}>
              <GenreTags genres={catalogSourcedGenre ? [catalogSourcedGenre] : (genres ?? [])} />
            </div>
          ) : null}
          {bio?.length ? (
            <RemoveFeature
              id={FeatureSetEnum.TMPerformerRemoveBioDiagnostic}
              canParticipate={() => isNaCobrands(appContext)}
              recordType={RecordParticipationTypes.IN_VIEW}
              feature={() => (
                <RemoveFeature
                  id={FeatureSetEnum.TMINTLRemoveBio}
                  canParticipate={() => !isNaCobrands(appContext)}
                  recordType={RecordParticipationTypes.IN_VIEW}
                  feature={() => (
                    <div className={resolvedSectionClasses}>
                      <TruncateText
                        isModal
                        modalTitle={performerName ?? ""}
                        maxLines={maxLinesForBio}
                        pageEventArgs={[PE_TYPE.CLICK, PE_KEYS.PERFORMER_INFO_CARD_BIO_SEE_MORE, performerName ?? ""]}
                        text={bio}
                      />
                    </div>
                  )}
                />
              )}
            />
          ) : null}
          {!!showDuration && (
            <RemoveFeature
              id={FeatureSetEnum.TMPerformerRemovePlaybillShowDuration}
              canParticipate={() => !!showDuration}
              recordType={RecordParticipationTypes.IN_VIEW}
              feature={() => <TheaterRuntime durationString={showDuration!} className={theaterRuntimeMargin} />}
            />
          )}
          <AddFeature
            id={FeatureSetEnum.TMPerformerReigningChampion}
            canParticipate={() => !!hasReigningChampionData}
            feature={() => <ReigningChampion reigningChampionData={reigningChampions} />}
          />
          {!isInfoCardModuleRemoved && !!youTubeVideoId && (
            <div className={videoContainerStyle}>
              <EmbeddedYoutubePlayer videoId={youTubeVideoId} />
            </div>
          )}
          {!isInfoCardModuleRemoved && !isMobile && !!spotifyTopTrackId && (
            <SpotifyEmbeddedPlayer trackId={spotifyTopTrackId} className={spotifyPlayerStyle} />
          )}
          {!!categoryUserGeneratedImages && (
            <UserContentCarousel
              images={categoryUserGeneratedImages}
              categoryName={performerName ?? ""}
              isSportsFanImages={isSportsCategory}
            />
          )}

          {(model.categoryMetadata?.common?.infoCardCarouselImageUrls?.length ?? 0) > 0 && (
            <ImageCarousel
              images={model.categoryMetadata?.common?.infoCardCarouselImageUrls ?? []}
              imageLabel={performerName ?? defaultImageAltText}
              autoRotate
            />
          )}

          <PerformerCardSocialLinks className={socialLinksStyle} />
        </div>
      )}
    </div>
  );
});
PerformerInfoCardDesktop.displayName = "PerformerInfoCardDesktop";
